import { useState, useEffect, useRef } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { ChevronDown, Zap, PanelLeftClose, PanelLeft, Sun, Moon, LogOut, User, MoreVertical, ShieldCheck } from 'lucide-react';
import { NAV_GROUPS } from '../config/navigation.js';
import { useAuth } from '../lib/auth.jsx';
import { hasAccess } from '../lib/rbac.js';
import { FloatingPanel } from '../components/FloatingPanel.jsx';
import { UserAvatar } from '../components/UserAvatar.jsx';

/**
 * Navegação lateral avançada.
 * - Scroll invisível (.no-scrollbar) com rolagem funcional.
 * - Ícones Lucide, tipografia Inter com pesos hierárquicos.
 * - Microinterações: hover, indent suave, indicador de página ativa.
 * - RBAC: grupos/itens com minLevel são ocultados por nível de acesso.
 * - Colapsável (rail) em telas grandes; overlay em mobile.
 */
export default function Sidebar({ open, onClose, collapsed, onToggleCollapse }) {
  const { level } = useAuth();

  return (
    <>
      {/* Overlay mobile */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 flex flex-col glass border-r border-border',
          'transition-all duration-300 ease-out',
          collapsed ? 'w-[76px]' : 'w-64',
          open ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        ].join(' ')}
      >
        {/* Brand */}
        <div className="relative flex flex-col items-center justify-center px-3 lg:px-4 py-4 h-auto shrink-0 border-b border-border">
          <div className="flex flex-col items-center gap-1 min-w-0">
            {!collapsed ? (
              <>
                <img src="/feederp-logo-claro.png" className="logo-claro w-auto h-24 object-contain" alt="Feed" />
                <img src="/feederp-logo-escuro.png" className="logo-escuro w-auto h-24 object-contain" alt="Feed" />
                <p className="text-xs font-500 text-text-muted mt-1 text-center">Gestão Unificada</p>
              </>
            ) : (
              <div className="grid place-items-center w-9 h-9 rounded-lg bg-accent-soft text-accent shrink-0">
                <Zap size={20} strokeWidth={2.5} />
              </div>
            )}
          </div>

          <button
            onClick={onToggleCollapse}
            className="absolute right-3 lg:right-4 top-4 hidden lg:grid place-items-center w-10 h-10 rounded-lg text-text-muted hover:text-text hover:bg-surface-hover transition-all shrink-0"
            title={collapsed ? 'Expandir menu' : 'Recolher menu'}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            {collapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        {/* Navegação rolável — scroll invisível */}
        <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-4 space-y-1">
          {NAV_GROUPS.map((group, gi) => {
            if (group.hidden) return null;
            if (!hasAccess(level, group.minLevel)) return null;
            const visibleItems = group.items.filter((it) => hasAccess(level, it.minLevel));
            if (visibleItems.length === 0) return null;
            return (
              <NavGroup
                key={gi}
                label={group.label}
                items={visibleItems}
                collapsed={collapsed}
                onNavigate={onClose}
              />
            );
          })}
        </nav>

        {/* Rodapé: Menu de Usuário */}
        <div className="shrink-0 border-t border-border p-3">
          <UserMenu collapsed={collapsed} onClose={onClose} />
        </div>

      </aside>
    </>
  );
}

function UserMenu({ collapsed, onClose }) {
  const { user, level, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || 'dark'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <>
      <button
        ref={anchorRef}
        onClick={() => setOpen((o) => !o)}
        title="Meu Perfil"
        className={[
          'flex items-center gap-3 px-2 py-2 w-full rounded-lg hover:bg-surface-hover transition-colors text-left',
          collapsed ? 'justify-center' : '',
        ].join(' ')}
      >
        <UserAvatar src={user?.foto_url} name={user?.nome} size={32} />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="text-sm font-600 text-text truncate">{user?.nome || 'Usuário'}</p>
            <p className="text-xs font-500 text-text-muted capitalize truncate">{level}</p>
          </div>
        )}
        {!collapsed && <MoreVertical size={16} className="text-text-muted shrink-0" />}
      </button>

      <FloatingPanel
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        align={collapsed ? 'start' : 'center'}
        minWidth={220}
        estimatedHeight={200}
        className="py-2 flex flex-col gap-1"
      >
        <div className="px-4 py-2 border-b border-border mb-1">
          <p className="text-sm font-600 text-text truncate">{user?.nome || 'Usuário'}</p>
          <p className="text-xs font-500 text-text-muted capitalize truncate">{level}</p>
        </div>

        <Link
          to="/meu-perfil"
          onClick={() => { setOpen(false); onClose(); }}
          className="flex items-center gap-3 px-4 py-2 text-sm font-500 text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
        >
          <User size={16} />
          Meu Perfil
        </Link>

        <button
          onClick={() => {
            setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
            // mantem aberto intencionalmente para feedback visual
          }}
          className="flex items-center gap-3 px-4 py-2 w-full text-left text-sm font-500 text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          Modo {theme === 'dark' ? 'Claro' : 'Escuro'}
        </button>

        <Link
          to="/privacidade"
          onClick={() => { setOpen(false); onClose(); }}
          className="flex items-center gap-3 px-4 py-2 text-sm font-500 text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
        >
          <ShieldCheck size={16} />
          Privacidade
        </Link>

        <div className="h-px bg-border my-1"></div>

        <button
          onClick={() => {
            setOpen(false);
            logout();
          }}
          className="flex items-center gap-3 px-4 py-2 w-full text-left text-sm font-600 text-danger hover:bg-danger/10 transition-colors"
        >
          <LogOut size={16} />
          Sair do Sistema
        </button>
      </FloatingPanel>
    </>
  );
}

function NavGroup({ label, items, collapsed, onNavigate }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div>
      {label && !collapsed && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-between w-full px-3 py-2 rounded-lg group transition-colors duration-200 hover:bg-surface-hover"
        >
          <span className="text-[11px] font-700 uppercase tracking-wider text-text-muted group-hover:text-text-secondary">
            {label}
          </span>
          <ChevronDown
            size={14}
            className={[
              'text-text-muted transition-transform duration-300',
              expanded ? '' : '-rotate-90',
            ].join(' ')}
          />
        </button>
      )}

      <div
        className={[
          'overflow-hidden transition-all duration-300 ease-out',
          expanded || collapsed ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
        ].join(' ')}
      >
        <div className="space-y-0.5 py-0.5">
          {items.map((item) => (
            <NavItem key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    </div>
  );
}

function NavItem({ item, collapsed, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-600',
          'transition-all duration-300 overflow-hidden',
          collapsed ? 'justify-center' : 'hover:translate-x-1',
          isActive
            ? 'text-text font-700 bg-gradient-to-r from-[var(--color-fc-lilas)]/15 to-transparent'
            : 'text-text-secondary hover:text-text hover:bg-surface-hover',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {/* Marcador Gradiente Brilhante Animado */}
          <span
            className={[
              'absolute left-0 top-0 bottom-0 w-1 rounded-r-full bg-gradient-to-b from-[var(--color-ft-blue)] to-[var(--color-fc-lilas)] transition-all duration-300 shadow-[0_0_12px_var(--color-fc-lilas)]',
              isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0',
            ].join(' ')}
            style={{ transformOrigin: 'center' }}
          />
          
          <Icon 
            size={18} 
            strokeWidth={isActive ? 2.5 : 2} 
            className={[
              'shrink-0 transition-all duration-300',
              isActive ? 'text-[var(--color-fc-lilas)] drop-shadow-[0_0_8px_var(--color-fc-lilas)]' : 'group-hover:text-text'
            ].join(' ')} 
          />
          
          {!collapsed && (
            <>
              <span className="truncate relative z-10 flex-1">{item.label}</span>
              {item.badge && (
                <span className="text-[10px] font-600 uppercase tracking-wide text-text-muted bg-surface-hover px-1.5 py-0.5 rounded shrink-0">
                  {item.badge}
                </span>
              )}
            </>
          )}
        </>
      )}
    </NavLink>
  );
}
